import type { ReactNode } from "react"
import type { NumericInputProps } from "../../reusable/Input"
import { ERP_CONTROLLED_TOOLTIP } from "~/constants/erpFields"
import { DisabledTooltip } from "../../reusable/DisabledTooltip"
import { NumericInput } from "../../reusable/Input"
import { ResetButton } from "./ResetButton"
import { FieldRow } from "./FieldRow"
import { ErpIndicator } from "./ErpIndicator"
import type { IFormData, MetadataForEdit } from "./types"

type NumericFieldRowProps = Omit<
  NumericInputProps,
  "value" | "onChange" | "placeholder" | "disabled"
> & {
  label: ReactNode
  fieldName: keyof IFormData
  formData: IFormData
  setFormData: (data: IFormData) => void
  metadataForEdit: MetadataForEdit
  getFieldBadges: (fieldName: keyof IFormData) => ReactNode
  shouldShowResetButton: (fieldName: keyof IFormData) => boolean
  handleResetToDefault: (fieldName: keyof IFormData) => void
  customPlaceholder?: string | number
}

export const NumericFieldRow = ({
  label,
  fieldName,
  formData,
  setFormData,
  metadataForEdit,
  getFieldBadges,
  shouldShowResetButton,
  handleResetToDefault,
  unit,
  customPlaceholder,
  ...numericInputProps
}: NumericFieldRowProps) => {
  const fieldData = metadataForEdit.fields[fieldName]
  const value = formData[fieldName] as number | null | undefined
  const showReset = shouldShowResetButton(fieldName)

  const getResetTooltip = () => {
    const erpValue = fieldData?.metadata?.erpValue
    const defaultValue = fieldData?.metadata?.defaultValue

    if (erpValue !== undefined && erpValue !== null) {
      return `Reset to ERP value: ${erpValue} ${unit}`
    }
    if (defaultValue !== undefined && defaultValue !== null) {
      return `Reset to company default: ${defaultValue} ${unit}`
    }
    return "Clear custom value"
  }

  const defaultValue = fieldData?.metadata?.defaultValue
  const displayValue = value ?? 0

  const getPlaceholder = () => {
    if (customPlaceholder !== undefined) {
      return customPlaceholder
    }
    if (fieldData?.isMultiple) {
      return "Mixed values"
    }
    if (defaultValue !== undefined && defaultValue !== null) {
      return defaultValue
    }
    return undefined
  }

  const erpIndicator = (
    <ErpIndicator
      erpValue={fieldData?.metadata?.erpValue}
      erpValues={fieldData?.metadata?.erpValues}
      unit={unit}
    />
  )

  return (
    <FieldRow label={label} badges={getFieldBadges(fieldName)} erpIndicator={erpIndicator}>
      <div className="flex items-center gap-2 w-full">
        <DisabledTooltip
          title={fieldData?.isEditable === false ? ERP_CONTROLLED_TOOLTIP : undefined}
          wrapperClassName="flex-1"
        >
          <NumericInput
            containerClassName="w-full"
            small
            unit={unit}
            value={displayValue}
            onChange={(newValue) =>
              setFormData({
                ...formData,
                [fieldName]: fieldName === "colliSize" && newValue === 0 ? null : newValue,
              })
            }
            placeholder={getPlaceholder()}
            disabled={!fieldData?.isEditable}
            {...numericInputProps}
          />
        </DisabledTooltip>
        {showReset && (
          <ResetButton
            onClick={() => handleResetToDefault(fieldName)}
            tooltip={getResetTooltip()}
          />
        )}
      </div>
    </FieldRow>
  )
}
